
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../integrations/supabase/client';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';
import { notificationSoundService } from '../services/notificationSoundService';
import { pushService } from '../services/pushService';

interface Notification {
  id: string;
  type: 'match' | 'message' | 'application' | 'tour' | 'system' | 'property_like';
  title: string;
  message: string;
  data?: any;
  read: boolean;
  created_at: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  action?: {
    label: string;
    url: string;
  };
}

interface NotificationStats {
  total: number;
  unread: number;
  byType: Record<string, number>;
}

export const useRealTimeNotifications = () => {
  const { user, profile } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [stats, setStats] = useState<NotificationStats>({ 
    total: 0, 
    unread: 0, 
    byType: {} 
  });
  const [loading, setLoading] = useState(false);

  // Helper function to validate and cast notification type
  const validateNotificationType = (type: string): Notification['type'] => {
    const validTypes: Notification['type'][] = ['match', 'message', 'application', 'tour', 'system', 'property_like'];
    return validTypes.includes(type as Notification['type']) ? type as Notification['type'] : 'system';
  };

  // Initialize push notifications when user is available
  useEffect(() => {
    if (user) {
      fetchNotifications();
      initializePushNotifications();
    }
  }, [user]);

  const initializePushNotifications = async () => {
    try {
      const initialized = await pushService.initialize();
      if (initialized) {
        const permission = await pushService.requestPermission();
        if (permission === 'granted') {
          await pushService.subscribeToPush();
          console.log('[useRealTimeNotifications] Push notifications initialized');
        }
      }
    } catch (error) {
      console.warn('[useRealTimeNotifications] Push initialization failed:', error);
    }
  };

  // Listen for real-time notifications
  useEffect(() => {
    if (!user) return;

    console.log('[useRealTimeNotifications] Setting up realtime subscription for user:', user.id);

    const channel = supabase
      .channel('notification_events')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notification_events',
          filter: `user_id=eq.${user.id}`
        },
        async (payload) => {
          console.log('[useRealTimeNotifications] New notification received:', payload);
          const newNotification = payload.new as any;
          const notification: Notification = {
            id: newNotification.id,
            type: validateNotificationType(newNotification.type),
            title: newNotification.title,
            message: newNotification.message,
            data: newNotification.data,
            read: newNotification.read,
            created_at: newNotification.created_at,
            priority: getPriority(newNotification.type),
            action: getAction(newNotification)
          };

          setNotifications(prev => [notification, ...prev]);
          
          // Play notification sound
          notificationSoundService.playNotificationSound(notification.type);
          
          // Show toast for all notifications
          toast({
            title: notification.title,
            description: notification.message,
            duration: notification.priority === 'urgent' ? 8000 : 5000,
          });

          // Send push notification via edge function
          try {
            await supabase.functions.invoke('send-push', {
              body: {
                user_id: user.id,
                title: notification.title,
                body: notification.message,
                data: notification.data,
                tag: notification.type
              }
            });
          } catch (pushError) {
            console.warn('[useRealTimeNotifications] Push notification failed:', pushError);
          }

          // Browser notification fallback
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification(notification.title, {
              body: notification.message,
              icon: '/favicon.ico',
              tag: notification.type,
              data: notification.data
            });
          } else if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission().then(permission => {
              if (permission === 'granted') {
                new Notification(notification.title, {
                  body: notification.message,
                  icon: '/favicon.ico',
                  tag: notification.type,
                  data: notification.data
                });
              }
            });
          }
        }
      )
      .subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          console.log('[useRealTimeNotifications] Successfully subscribed to notifications');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('[useRealTimeNotifications] Channel error:', err);
        } else if (status === 'TIMED_OUT') {
          console.error('[useRealTimeNotifications] Connection timed out');
        } else if (status === 'CLOSED') {
          // Suppress "Connection closed" warnings in development (React Strict Mode causes frequent reconnects)
          if (process.env.NODE_ENV === 'production') {
            console.warn('[useRealTimeNotifications] Connection closed');
          }
        }
      });

    return () => {
      console.log('[useRealTimeNotifications] Cleaning up realtime subscription');
      supabase.removeChannel(channel);
    };
  }, [user]);

  const fetchNotifications = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('notification_events')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      const formattedNotifications: Notification[] = (data || []).map(item => ({
        id: item.id,
        type: validateNotificationType(item.type),
        title: item.title,
        message: item.message,
        data: item.data,
        read: item.read,
        created_at: item.created_at,
        priority: getPriority(item.type),
        action: getAction(item)
      }));

      setNotifications(formattedNotifications);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPriority = (type: string): Notification['priority'] => {
    switch (type) {
      case 'match':
      case 'application':
        return 'high';
      case 'message':
      case 'tour':
      case 'property_like':
        return 'medium';
      default:
        return 'low';
    }
  };

  const getAction = (notification: any): Notification['action'] | undefined => {
    switch (notification.type) {
      case 'match':
        return {
          label: 'View Match',
          url: `/landlord-dashboard?highlight=matches`
        };
      case 'property_like':
        return {
          label: 'View Property',
          url: `/property-management/${notification.data?.property_id}`
        };
      case 'message': {
        // Route to appropriate messaging interface based on user type
        const messageUrl = profile?.user_type === 'landlord'
          ? `/landlord-messages?conversation=${notification.data?.conversation_id}`
          : `/messages?conversation=${notification.data?.conversation_id}`;
        return {
          label: 'View Message',
          url: messageUrl
        };
      }
      case 'application':
        return {
          label: 'View Application',
          url: `/applications/${notification.data?.application_id}`
        };
      case 'tour':
        return {
          label: 'View Tour',
          url: `/tours/${notification.data?.tour_id}`
        };
      default:
        return undefined;
    }
  };

  // Update stats when notifications change
  useEffect(() => {
    const unread = notifications.filter(n => !n.read).length;
    const byType = notifications.reduce((acc, n) => {
      acc[n.type] = (acc[n.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    setStats({
      total: notifications.length,
      unread,
      byType
    });
  }, [notifications]);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notification_events')
        .update({ read: true })
        .eq('id', notificationId)
        .eq('user_id', user!.id);

      if (error) throw error;

      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, read: true } : n
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, [user]);

  const markAllAsRead = useCallback(async () => {
    try {
      const { error } = await supabase
        .from('notification_events')
        .update({ read: true })
        .eq('user_id', user!.id)
        .eq('read', false);

      if (error) throw error;

      setNotifications(prev => 
        prev.map(n => ({ ...n, read: true }))
      );
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, [user]);

  const removeNotification = useCallback(async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notification_events')
        .delete()
        .eq('id', notificationId)
        .eq('user_id', user!.id);

      if (error) throw error;

      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error removing notification:', error);
    }
  }, [user]);

  const clearAll = useCallback(async () => {
    try {
      const { error } = await supabase
        .from('notification_events')
        .delete()
        .eq('user_id', user!.id);

      if (error) throw error;

      setNotifications([]);
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  }, [user]);

  return {
    notifications,
    stats,
    loading,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAll,
    refetch: fetchNotifications
  };
};
